import React, { useRef, useEffect } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';

const TYPES = ['Form', 'Table', 'App', 'Sign', 'Workflow', 'Agent', 'Report'];

export const EMPTY_FILTERS = { types: [], status: '', starred: '', team_id: '', date_from: '', date_to: '' };

export const countActiveFilters = (f) =>
  (f.types.length ? 1 : 0) + (f.status ? 1 : 0) + (f.starred ? 1 : 0) + (f.team_id ? 1 : 0) + (f.date_from || f.date_to ? 1 : 0);

const Chip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors ${
      active ? 'bg-[#0A1551] text-white border-[#0A1551]' : 'bg-white text-[#0A1551] border-[#D5D7ED] hover:bg-[#F5F6FB]'
    }`}
  >
    {children}
  </button>
);

const Label = ({ children }) => <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6F76A7] mb-1.5">{children}</div>;

const selectCls = 'w-full px-2.5 py-1.5 border border-[#D5D7ED] rounded-md text-[13px] text-[#0A1551] bg-white focus:outline-none focus:border-[#0A1551]';

const FilterDropdown = ({ open, setOpen, filters, setFilters, teams, hideTeam }) => {
  const ref = useRef(null);
  const count = countActiveFilters(filters);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen]);

  const toggleType = (t) =>
    setFilters((f) => ({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] }));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="filter-button"
        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[14px] font-medium transition-colors ${
          count ? 'border-[#0A1551] bg-[#F0F1FB] text-[#0A1551]' : 'border-[#D5D7ED] text-[#0A1551] hover:bg-[#F5F6FB]'
        }`}
      >
        <SlidersHorizontal className="w-4 h-4" />
        Filter
        {count > 0 && <span data-testid="filter-count" className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0F766E] text-white text-[11px] font-bold flex items-center justify-center">{count}</span>}
      </button>

      {open && (
        <div data-testid="filter-dropdown" className="absolute left-0 top-full mt-2 z-40 w-[340px] bg-white border border-[#E3E5F5] rounded-xl shadow-xl p-4 space-y-4">
          <div>
            <Label>Type</Label>
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((t) => <Chip key={t} active={filters.types.includes(t)} onClick={() => toggleType(t)} testId={`filter-type-${t.toLowerCase()}`}>{t}</Chip>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={selectCls}>
                <option value="">Any</option><option value="Enabled">Enabled</option><option value="Disabled">Disabled</option>
              </select>
            </div>
            <div>
              <Label>Starred</Label>
              <select data-testid="filter-starred" value={filters.starred} onChange={(e) => setFilters((f) => ({ ...f, starred: e.target.value }))} className={selectCls}>
                <option value="">Any</option><option value="true">Starred only</option><option value="false">Not starred</option>
              </select>
            </div>
          </div>
          {!hideTeam && (
            <div>
              <Label>Workspace</Label>
              <select data-testid="filter-team" value={filters.team_id} onChange={(e) => setFilters((f) => ({ ...f, team_id: e.target.value }))} className={selectCls}>
                <option value="">All workspaces</option>
                <option value="personal">Personal only</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label>Last activity</Label>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className={selectCls} />
              <input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className={selectCls} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-[#E3E5F5]">
            <button data-testid="filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} className="flex items-center gap-1 text-[13px] text-[#6F76A7] hover:text-[#E5387B]">
              <X className="w-3.5 h-3.5" /> Clear all
            </button>
            <button data-testid="filter-done" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-md bg-[#0A1551] text-white text-[13px] font-semibold">Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
